import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useSelector } from 'react-redux';
import {
  ArrowDownTrayIcon,
  ChartBarIcon,
  CubeIcon,
  SparklesIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import dashboardApi from '../../services/dashboardApi';
import storeApi from '../../services/storeApi';
import terminalApi from '../../services/terminalApi';
import orderApi from '../../services/orderApi';
import NoData2 from '../../assets/no-data-2.svg';
import Notification from '../../components/notification/notification';
import { setStartAndEndDate } from '../../utils/setStartAndEndDate';
import {
  PageHeader,
  SectionHeading,
  EmptyState,
  TWButton,
} from '../../components/ui';
import DownloadCsvModal from './components/DownloadCsvModal';
import FilterBar from './components/filter-bar';
import KpiGrid from './components/kpi-grid';
import ProductsTable from './components/products-table';
import TipCalculator from './components/tip-calculator';

const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');
dayjs.Ls.en.weekStart = 1;
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const defaultRange = () => ({
  startdate: setStartAndEndDate(
    dayjs().subtract(7, 'day').startOf('day'),
    'startdate',
    '0',
  ),
  enddate: setStartAndEndDate(dayjs(), 'enddate', '0'),
  storeids: [],
});

const ReportStore = () => {
  const reduxCompany = useSelector((state) => state.companyReducer.selected);
  const [globalCompany] = useState(reduxCompany);

  const [filteredList, setFilteredList] = useState([]);
  const [productUpsellFilteredList, setProductUpsellFilteredList] = useState(
    [],
  );

  const [reportSearch, setReportSearch] = useState(defaultRange);
  const [productSearch, setProductSearch] = useState(defaultRange);
  const [productUpsellSearch, setProductUpsellSearch] = useState(defaultRange);

  const [selectedTipRange, setSelectedTipRange] = useState({
    startdate: setStartAndEndDate(
      dayjs().subtract(7, 'day').startOf('day'),
      'startdate',
      '0',
    ),
    enddate: setStartAndEndDate(dayjs(), 'enddate', '0'),
    startTime: '00:00',
    endTime: '23:59',
    storeids: [],
  });

  const [calculateTip, setCalculateTip] = useState({
    totalTip: 0,
    orderCount: 0,
  });

  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [loading, setLoading] = useState({
    topOrderItems: true,
    productUpsellItems: true,
    totalTip: false,
    csvLoading: false,
    clickCountLoading: false,
    chart: true,
  });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });
  const [upsellPagination, setUpsellPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });

  const [dialogs, setDialogs] = useState({ csv: false });
  const [csvForm, setCsvForm] = useState({
    startdate: setStartAndEndDate(
      dayjs().subtract(60, 'day').startOf('day'),
      'startdate',
      '0',
    ),
    enddate: setStartAndEndDate(dayjs(), 'enddate', '0'),
    storeid: '',
  });

  const [showReportCustom, setShowReportCustom] = useState(false);
  const [showProductCustom, setShowProductCustom] = useState(false);
  const [showUpsellCustom, setShowUpsellCustom] = useState(false);

  const [topOrderItems, setTopOrderItems] = useState([]);
  const [productUpsellItems, setProductUpsellItems] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [orderInformations, setOrderInformations] = useState({
    orderTotal: 0,
    orderCount: 0,
    orderRefundTotal: 0,
    orderDaily: [],
    orderTop10: [],
    orderCountByTerminal: [],
    totalClickCount: 0,
    clickCount: 0,
    allOrderCount: 0,
    tip: 0,
    refund: 0,
    ordertotal: 0,
    profit: 0,
    tax: 0,
    payout: 0,
  });

  const [sortConfig, setSortConfig] = useState({ key: null, orderby: 0 });

  const chart = useMemo(
    () => ({
      series: [
        {
          name: 'Sales',
          data: (orderInformations.orderDaily || []).map((i) => i.total),
        },
      ],
      options: {
        colors: ['#2563eb'],
        legend: { show: false },
        grid: {
          borderColor: 'rgba(100, 116, 139, 0.12)',
          strokeDashArray: 4,
          padding: { left: 10, right: 10, bottom: 4 },
        },
        chart: {
          height: 340,
          type: 'area',
          toolbar: { show: false },
          fontFamily: 'inherit',
          background: 'transparent',
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.05,
            stops: [0, 90, 100],
          },
        },
        yaxis: {
          labels: {
            style: { colors: '#64748b', fontSize: '11px' },
            formatter: (value) => `$${parseFloat(value).toFixed(0)}`,
          },
        },
        xaxis: {
          type: 'date',
          categories: (orderInformations.orderDaily || []).map((i) => i.date),
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
        tooltip: {
          x: { format: 'MM/DD/YYYY' },
          y: {
            formatter: (value) =>
              value.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              }),
          },
          theme: 'light',
        },
      },
    }),
    [orderInformations.orderDaily],
  );

  const handleDateRangeSelectChange = (key, selected) => {
    if (!selected) return;
    const update = (setter, setShowCustom) => {
      let enddate = dayjs();
      let startdate;
      setShowCustom(false);

      switch (selected) {
        case 'today':
          startdate = enddate.startOf('day');
          break;
        case 'yesterday':
          startdate = enddate.subtract(1, 'day').startOf('day');
          enddate = enddate.subtract(1, 'day').endOf('day');
          break;
        case 'weekToD‌​ate':
          startdate = enddate.startOf('week');
          break;
        case 'last7Days':
          startdate = enddate.subtract(7, 'day').startOf('day');
          break;
        case 'last14Days':
          startdate = enddate.subtract(14, 'day').startOf('day');
          break;
        case 'last21Days':
          startdate = enddate.subtract(21, 'day').startOf('day');
          break;
        case 'last28Days':
          startdate = enddate.subtract(28, 'day').startOf('day');
          break;
        case 'last30Days':
          startdate = enddate.subtract(30, 'day').startOf('day');
          break;
        case 'monthToD‌​ate':
          startdate = enddate.startOf('month');
          break;
        case 'last60Days':
          startdate = enddate.subtract(60, 'day').startOf('day');
          break;
        case 'customRange':
          setShowCustom(true);
          return;
        default:
          return;
      }

      setter((prev) => ({
        ...prev,
        startdate: setStartAndEndDate(startdate, 'startdate', '0'),
        enddate: setStartAndEndDate(enddate, 'enddate', '0'),
      }));
    };

    if (key === 'report') update(setReportSearch, setShowReportCustom);
    else if (key === 'upsell')
      update(setProductUpsellSearch, setShowUpsellCustom);
    else update(setProductSearch, setShowProductCustom);
  };

  const onDateChange = (key, dateString) => {
    const update = (setter) =>
      setter((prev) => ({
        ...prev,
        startdate: setStartAndEndDate(dateString[0], 'startdate', '0'),
        enddate: setStartAndEndDate(dateString[1], 'enddate', '0'),
      }));
    if (key === 'report') update(setReportSearch);
    else if (key === 'upsell') update(setProductUpsellSearch);
    else update(setProductSearch);
  };

  const onSelectStore = (key, storeid) => {
    const storeids = storeid ? [storeid] : [];
    const update = (state, setter) =>
      setter((prev) => ({
        ...prev,
        startdate: setStartAndEndDate(state.startdate, 'startdate', '0'),
        enddate: setStartAndEndDate(state.enddate, 'enddate', '0'),
        storeids,
      }));
    if (key === 'report') update(reportSearch, setReportSearch);
    else if (key === 'upsell')
      update(productUpsellSearch, setProductUpsellSearch);
    else update(productSearch, setProductSearch);
  };

  const notify = (message) =>
    setNotf((prev) => ({ ...prev, message, display: true }));

  const getTopOrderItems = async () => {
    try {
      const query = { ...productSearch, companyid: globalCompany.id };
      const list = await dashboardApi.getTopOrderItems(query);
      const data = list.data.data || [];
      setTopOrderItems(data);
      setFilteredList(data.slice(0, pagination.pageSize));
      setPagination((prev) => ({
        ...prev,
        totalRecords: Math.ceil(data.length),
      }));
    } catch (error) {
      notify(error);
    } finally {
      setLoading((prev) => ({ ...prev, topOrderItems: false }));
    }
  };

  const requestSort = (key) => {
    let orderby = 1;
    if (sortConfig.key === key && sortConfig.orderby === 1) orderby = 0;
    setSortConfig({ key, orderby });

    const sorted = [...topOrderItems]
      .sort((a, b) => {
        if (a[key] < b[key]) return orderby === 1 ? -1 : 1;
        if (a[key] > b[key]) return orderby === 1 ? 1 : -1;
        return 0;
      })
      .slice(
        (pagination.pageNumber - 1) * pagination.pageSize,
        pagination.pageSize * pagination.pageNumber,
      );
    setFilteredList(sorted);
  };

  const handleChangePage = (pageNumber, pageSize) => {
    setPagination((prev) => ({ ...prev, pageNumber, pageSize }));
    const startIndex = (pageNumber - 1) * pageSize;
    setFilteredList(topOrderItems.slice(startIndex, startIndex + pageSize));
  };

  const handleUpsellChangePage = (pageNumber, pageSize) => {
    setUpsellPagination((prev) => ({ ...prev, pageNumber, pageSize }));
    const startIndex = (pageNumber - 1) * pageSize;
    setProductUpsellFilteredList(
      productUpsellItems.slice(startIndex, startIndex + pageSize),
    );
  };

  const getStoreList = async () => {
    try {
      const query = globalCompany.id ? `companyid=${globalCompany.id}` : '';
      const list = await storeApi.list(query);
      const stores = list.data.data || [];
      setStoreList(stores);
      // Default-select the first store across all filter bars + tip calculator
      // when nothing is selected yet.
      if (stores.length > 0) {
        const firstId = stores[0].id;
        const apply = (setter) =>
          setter((prev) =>
            prev.storeids && prev.storeids.length > 0
              ? prev
              : { ...prev, storeids: [firstId] },
          );
        apply(setReportSearch);
        apply(setProductSearch);
        apply(setProductUpsellSearch);
        apply(setSelectedTipRange);
      }
    } catch (error) {
      notify(error);
      setStoreList([]);
    }
  };

  const getOrderTotal = async () => {
    try {
      const query = { ...reportSearch, companyid: globalCompany.id };
      const list = await dashboardApi.getOrderTotal(query);
      setOrderInformations((prev) => ({
        ...prev,
        orderTotal: list.data.data || 0,
      }));
    } catch (error) {
      notify(error);
    }
  };

  const getReports = async () => {
    try {
      const query = { ...reportSearch, companyid: globalCompany.id };
      const res = await dashboardApi.getReports(query);
      const counts = res.data.counts[0] || {};
      const {
        tip = 0,
        refund = 0,
        ordertotal = 0,
        profit = 0,
        tax = 0,
        payout = 0,
      } = counts;
      setOrderInformations((prev) => ({
        ...prev,
        tip,
        refund,
        ordertotal,
        profit,
        tax,
        payout,
      }));
    } catch (error) {
      notify(error?.message || 'Failed to load reports');
    }
  };

  const getOrdersDaily = async () => {
    setLoading((prev) => ({ ...prev, chart: true }));
    try {
      const query = { ...reportSearch, companyid: globalCompany.id };
      const list = await dashboardApi.getOrdersDaily(query);
      setOrderInformations((prev) => ({
        ...prev,
        orderDaily: list.data.data || [],
      }));
    } catch (error) {
      notify(error);
    } finally {
      setLoading((prev) => ({ ...prev, chart: false }));
    }
  };

  const getOrderCount = async () => {
    try {
      const query = { ...reportSearch, companyid: globalCompany.id };
      const list = await dashboardApi.getOrderCount(query);
      setOrderInformations((prev) => ({
        ...prev,
        orderCount: list.data.data || 0,
      }));
    } catch (error) {
      notify(error);
    }
  };

  const getAllOrderCount = async () => {
    try {
      const query = {
        companyid: globalCompany.id,
        enddate: dayjs().add(1, 'day'),
        startdate: '2020-01-01',
      };
      const list = await dashboardApi.getOrderCount(query);
      setOrderInformations((prev) => ({
        ...prev,
        allOrderCount: list.data.data || 0,
      }));
    } catch (error) {
      notify(error);
    }
  };

  const getOrderRefunds = async () => {
    try {
      const query = { ...reportSearch, companyid: globalCompany.id };
      const list = await dashboardApi.getOrderRefundTotal(query);
      setOrderInformations((prev) => ({
        ...prev,
        orderRefundTotal: list.data.data || 0,
      }));
    } catch (error) {
      notify(error);
    }
  };

  const getTotalClickCountKiosk = async () => {
    setLoading((prev) => ({ ...prev, clickCountLoading: true }));
    try {
      const query = `?pageSize=1000&addclickcount=true&companyid=${globalCompany.id}`;
      const list = await terminalApi.list(query);
      const total = (list.data.data || []).reduce(
        (a, b) => a + (b.clickcount || 0),
        0,
      );
      setOrderInformations((prev) => ({ ...prev, totalClickCount: total }));
    } catch (error) {
      notify(error);
    } finally {
      setLoading((prev) => ({ ...prev, clickCountLoading: false }));
    }
  };

  const getOrderCountByTerminal = async () => {
    try {
      const query = { ...reportSearch, companyid: globalCompany.id };
      const list = await dashboardApi.getOrderCountByTerminal(query);
      setOrderInformations((prev) => ({
        ...prev,
        orderCountByTerminal: list.data.data || [],
      }));
    } catch (error) {
      notify(error);
    }
  };

  const getClickCountKiosk = async () => {
    try {
      const query = `?pageSize=1000&startdate=${reportSearch.startdate}&enddate=${reportSearch.enddate}&addclickcount=true&companyid=${globalCompany.id}`;
      const list = await terminalApi.list(query);
      const total = (list.data.data || []).reduce(
        (a, b) => a + (b.clickcount || 0),
        0,
      );
      setOrderInformations((prev) => ({ ...prev, clickCount: total }));
    } catch (error) {
      notify(error);
    }
  };

  const getUpsellOrderItems = async () => {
    setLoading((prev) => ({ ...prev, productUpsellItems: true }));
    try {
      const query = { ...productUpsellSearch, companyid: globalCompany.id };
      const list = await dashboardApi.getUpsellOrderItems(query);
      const data = list.data.data || [];
      setProductUpsellItems(data);
      setProductUpsellFilteredList(data.slice(0, upsellPagination.pageSize));
      setUpsellPagination((prev) => ({
        ...prev,
        totalRecords: Math.ceil(data.length),
      }));
    } catch (error) {
      notify(error);
    } finally {
      setLoading((prev) => ({ ...prev, productUpsellItems: false }));
    }
  };

  const onClickSearchTotalTip = async () => {
    const { startdate, enddate, startTime, endTime, storeids } =
      selectedTipRange;
    const start = dayjs(startdate);
    const end = dayjs(enddate);
    const diff = end.diff(start, 'day');

    if (diff >= 14) {
      notify('Date range must be less than 14 days');
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, totalTip: true }));
      let current = dayjs(startdate);
      const final = dayjs(enddate);
      let totalTip = 0;
      let orderCount = 0;

      while (current.isSameOrBefore(final, 'day')) {
        const formattedStart = `${current.format('YYYY-MM-DD')} ${startTime}`;
        const formattedEnd = `${current.format('YYYY-MM-DD')} ${endTime}`;
        const query = {
          startdate: formattedStart,
          enddate: formattedEnd,
          storeids,
          companyid: globalCompany.id,
        };
        // eslint-disable-next-line no-await-in-loop
        const response = await dashboardApi.getReports(query);
        if (response?.data) {
          totalTip += response.data.counts[0]?.tip || 0;
          orderCount += response.data.counts[0]?.ordercount || 0;
        }
        current = current.add(1, 'day');
      }

      setCalculateTip({ totalTip, orderCount });
    } catch (error) {
      notify(error.message || 'Something went wrong');
    } finally {
      setLoading((prev) => ({ ...prev, totalTip: false }));
    }
  };

  const handleTipRangeSelectChange = (value, type) => {
    if (type === 'date') {
      setSelectedTipRange((prev) => ({
        ...prev,
        startdate: dayjs(value[0]).format('YYYY-MM-DD'),
        enddate: dayjs(value[1]).format('YYYY-MM-DD'),
      }));
    } else if (type === 'time') {
      const [startTime, endTime] = value;
      setSelectedTipRange((prev) => ({
        ...prev,
        startTime: dayjs(startTime).format('HH:mm'),
        endTime: dayjs(endTime).format('HH:mm'),
      }));
    } else {
      setSelectedTipRange((prev) => ({ ...prev, storeids: [value] }));
    }
  };

  const onCloseCsvModal = () => setDialogs((prev) => ({ ...prev, csv: false }));
  const onOpenCsvModal = () => setDialogs((prev) => ({ ...prev, csv: true }));

  const onClickDownloadCSV = async () => {
    try {
      setLoading((prev) => ({ ...prev, csvLoading: true }));
      const query = `?startdate=${csvForm.startdate}&enddate=${csvForm.enddate}&storeid=${csvForm.storeid}`;
      const csvUrl = await orderApi.exportCsv(query);
      window.open(csvUrl.data, '_blank');
      onCloseCsvModal();
    } catch (error) {
      notify(error);
    } finally {
      setLoading((prev) => ({ ...prev, csvLoading: false }));
    }
  };

  const onChangeCsvForm = (value, key) => {
    if (key === 'date') {
      setCsvForm((prev) => ({
        ...prev,
        startdate: value ? value[0] : null,
        enddate: value ? value[1] : null,
      }));
    } else {
      setCsvForm((prev) => ({ ...prev, [key]: value }));
    }
  };

  useEffect(() => {
    if (!globalCompany?.id) return;
    getOrdersDaily();
    getOrderTotal();
    getOrderRefunds();
    getTotalClickCountKiosk();
    getOrderCount();
    getOrderCountByTerminal();
    getClickCountKiosk();
    getReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany, reportSearch]);

  useEffect(() => {
    if (!globalCompany?.id) return;
    getTopOrderItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearch]);

  useEffect(() => {
    if (!globalCompany?.id) return;
    getUpsellOrderItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productUpsellSearch]);

  useEffect(() => {
    if (!globalCompany?.id) return;
    getStoreList();
    getAllOrderCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany]);

  const minutesSpent = (orderInformations.allOrderCount * 4.25).toLocaleString(
    'en-US',
    { maximumFractionDigits: 0 },
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Reports"
          description="Track sales performance, product mix, and tip payouts across your stores."
          actions={
            <TWButton
              size="lg"
              icon={ArrowDownTrayIcon}
              label={loading.csvLoading ? 'Exporting…' : 'Download CSV'}
              disabled={loading.csvLoading}
              onClick={onOpenCsvModal}
            />
          }
        />

        <div className="space-y-10">
          <section className="space-y-5">
            <SectionHeading icon={ChartBarIcon} title="Sales overview" />

            <FilterBar
              search={reportSearch}
              storeList={storeList}
              disabled={!globalCompany.id}
              showCustomRange={showReportCustom}
              onRangeSelect={(v) => handleDateRangeSelectChange('report', v)}
              onDateChange={(e) => onDateChange('report', e)}
              onStoreSelect={(e) => onSelectStore('report', e)}
            />

            <div>
              {loading.chart ? (
                <div className="relative h-[340px] w-full overflow-hidden rounded-md bg-slate-100/70 dark:bg-slate-800/40">
                  <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/40 to-transparent dark:via-slate-700/30" />
                  <div className="absolute inset-x-6 bottom-6 flex items-end justify-between gap-1.5">
                    {[40, 65, 50, 80, 45, 70, 55, 90, 60, 75, 50, 85].map(
                      (h, i) => (
                        <div
                          key={i}
                          className="flex-1 animate-pulse rounded-t bg-slate-200/80 dark:bg-slate-700/60"
                          style={{
                            height: `${h}%`,
                            animationDelay: `${i * 80}ms`,
                          }}
                        />
                      ),
                    )}
                  </div>
                </div>
              ) : orderInformations.orderTotal > 0 ? (
                <ReactApexChart
                  options={chart.options}
                  series={chart.series}
                  type="area"
                  height={340}
                />
              ) : (
                <EmptyState
                  variant="plain"
                  image={NoData2}
                  title="No sales in this range"
                  description="Try adjusting the date range or picking a different store."
                />
              )}
            </div>

            <KpiGrid
              info={orderInformations}
              clickCountLoading={loading.clickCountLoading}
            />
          </section>

          <section className="relative overflow-hidden rounded-3xl bg-slate-900 p-10 text-center shadow-xl sm:p-14 dark:bg-slate-950">
            <div
              className="absolute inset-0 opacity-50"
              aria-hidden="true"
              style={{
                background:
                  'radial-gradient(circle at 30% 20%, rgba(59,130,246,0.35), transparent 55%), radial-gradient(circle at 75% 80%, rgba(56,189,248,0.25), transparent 60%)',
              }}
            />
            <div className="relative mx-auto max-w-2xl space-y-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-sky-200 ring-1 ring-inset ring-white/15">
                <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
                Customer impact
              </span>
              <h3 className="text-5xl font-bold tracking-tight text-white sm:text-6xl">
                {minutesSpent} <span className="text-sky-300">mins</span>
              </h3>
              <p className="text-base leading-relaxed text-slate-300">
                spent by your customers ordering on Thisway.
              </p>
            </div>
          </section>

          <section className="space-y-5">
            <SectionHeading icon={CubeIcon} title="Product sales" />

            <FilterBar
              search={productSearch}
              storeList={storeList}
              disabled={!globalCompany.id}
              showCustomRange={showProductCustom}
              onRangeSelect={(v) => handleDateRangeSelectChange('product', v)}
              onDateChange={(e) => onDateChange('product', e)}
              onStoreSelect={(e) => onSelectStore('product', e)}
            />

            <ProductsTable
              items={filteredList}
              loading={loading.topOrderItems}
              pagination={pagination}
              sortConfig={sortConfig}
              onSort={requestSort}
              onPageChange={handleChangePage}
            />
          </section>

          <section className="space-y-5">
            <SectionHeading icon={SparklesIcon} title="Upsell items" />

            <FilterBar
              search={productUpsellSearch}
              storeList={storeList}
              disabled={!globalCompany.id}
              showCustomRange={showUpsellCustom}
              onRangeSelect={(v) => handleDateRangeSelectChange('upsell', v)}
              onDateChange={(e) => onDateChange('upsell', e)}
              onStoreSelect={(e) => onSelectStore('upsell', e)}
            />

            <ProductsTable
              items={productUpsellFilteredList}
              loading={loading.productUpsellItems}
              pagination={upsellPagination}
              sortConfig={sortConfig}
              onSort={requestSort}
              onPageChange={handleUpsellChangePage}
            />
          </section>

          <section className="space-y-5 pb-4">
            <SectionHeading icon={BanknotesIcon} title="Tip calculator" />
            <TipCalculator
              selectedTipRange={selectedTipRange}
              storeList={storeList}
              calculateTip={calculateTip}
              loading={loading.totalTip}
              disabled={!globalCompany.id}
              onChange={handleTipRangeSelectChange}
              onCalculate={onClickSearchTotalTip}
            />
          </section>
        </div>
      </div>

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />

      <DownloadCsvModal
        show={dialogs.csv}
        onClose={onCloseCsvModal}
        loading={loading.csvLoading}
        onSubmit={onClickDownloadCSV}
        onChange={onChangeCsvForm}
        csvForm={csvForm}
        storeList={storeList}
      />
    </div>
  );
};

export default ReportStore;
